/**
 * Real-time Search Update Signal Hook
 *
 * Subscribes to user's notification channel and listens for 'search_update'
 * signals that indicate new restaurant results are available from H3 ring expansion.
 */

import { useCallback } from 'react';
import { useAppSelector } from '@/store/store';
import { selectCurrentUserId } from '@/store/slices/authSlice';
import { useAblyChannel } from './useAblyChannel';
import { useIsRealtimeActive } from './AblyProvider';

/**
 * Search update hint payload sent by the backend after each H3 ring expansion completes.
 */
export interface SearchUpdateHint {
  /** Unique search session ID from initial search */
  search_id: string;
  /** Which ring just completed (1, 2, or 3) */
  k: number;
  /** Number of new restaurants found in this ring */
  new_count: number;
  /** Whether this is the final ring (k=3) */
  is_final: boolean;
  /** Set to true if expansion stopped due to API quota exceeded */
  quota_exceeded?: boolean;
}

interface SearchUpdateSignal {
  type: 'search_update';
  notificationType: string;
  notificationId: number;
  timestamp: number;
  hint: SearchUpdateHint;
}

interface UseSearchUpdateSignalOptions {
  /** Search session ID to filter signals for */
  searchId: string | null;
  /** Callback when search update signal is received */
  onUpdate: (hint: SearchUpdateHint) => void;
  /** Whether to enable the subscription */
  enabled?: boolean;
}

interface UseSearchUpdateSignalReturn {
  /** Whether real-time search updates are active */
  isRealtimeActive: boolean;
}

/**
 * Subscribe to real-time search update signals
 *
 * When the backend completes expanding a new H3 ring, it sends a signal
 * via Ably. This hook listens for those signals and calls the onUpdate
 * callback with information about the newly available results.
 *
 * @example
 * ```tsx
 * useSearchUpdateSignal({
 *   searchId: 'a1b2c3d4',
 *   onUpdate: async (hint) => {
 *     if (hint.new_count > 0) {
 *       const expanded = await getExpandedSearchResults(hint.search_id, hint.k);
 *       // Append to existing results
 *     }
 *     if (hint.is_final) {
 *       setHasMoreRings(false);
 *     }
 *   },
 * });
 * ```
 */
export function useSearchUpdateSignal({
  searchId,
  onUpdate,
  enabled = true,
}: UseSearchUpdateSignalOptions): UseSearchUpdateSignalReturn {
  const userId = useAppSelector(selectCurrentUserId);
  const isRealtimeActive = useIsRealtimeActive();

  const handleSearchSignal = useCallback(
    (message: { data: SearchUpdateSignal }) => {
      // Only process search_update signals for our search session
      if (
        message.data.notificationType === 'search_update' &&
        message.data.hint?.search_id === searchId
      ) {
        console.log('[Realtime] Search update signal received:', message.data.hint);
        onUpdate(message.data.hint);
      }
    },
    [searchId, onUpdate]
  );

  useAblyChannel<SearchUpdateSignal>({
    channelName: userId ? `user-${userId}-notifications` : '',
    eventName: 'signal',
    onMessage: handleSearchSignal,
    enabled: !!userId && !!searchId && enabled && isRealtimeActive,
  });

  return {
    isRealtimeActive,
  };
}
